/**
 * Data Upload Page
 *
 * Admin page for uploading financial data CSV files and managing the
 * 3-stage pipeline (Load → Indicators → Ratings).
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  listBatches,
  deleteBatch,
  runStage1,
  runStage2,
  runStage3,
  getJobStatus,
  getBatch,
  getBatchResults,
  checkPeriod,
  clearCaches,
  initUpload,
  uploadFileToGCS,
  finalizeBatch,
  approvePreflight,
} from '../../../services/dataUploadService';
import type {
  UploadBatch,
  EntityType,
  BatchStatus,
  BatchFile,
  BatchResultsResponse,
  PreflightAnalysis,
} from '../../../types/dataUpload';
import { ENTITY_TYPE_LABELS, BATCH_STATUS_LABELS } from '../../../types/dataUpload';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import './DataUpload.css';

// ============================================================================
// CONSTANTS
// ============================================================================

const MAX_FILES = 5;
const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB (customer balance sheet files can be 300-400MB)
const ACCEPTED_EXTENSIONS = ['.csv', '.txt', '.xlsx', '.xlsm'];

const ENTITY_TYPES: EntityType[] = [
  'cooperativas_s1',
  'cooperativas_s2',
  'cooperativas_s3',
  'mutualistas',
  'bancos_privados',
];

// ============================================================================
// HELPERS
// ============================================================================

function parseTimestamp(ts: string | { _seconds: number; _nanoseconds: number } | null): Date | null {
  if (!ts) return null;
  if (typeof ts === 'string') return new Date(ts);
  if (typeof ts === 'object' && '_seconds' in ts) return new Date(ts._seconds * 1000);
  return null;
}

function formatDate(ts: string | { _seconds: number; _nanoseconds: number } | null): string {
  const date = parseTimestamp(ts);
  if (!date) return '—';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getStatusCategory(status: BatchStatus): string {
  if (status === 'preflight_review') return 'review';
  if (status === 'uploaded') return 'uploaded';
  if (status.endsWith('_queued')) return 'queued';
  if (status.endsWith('_running')) return 'running';
  if (status.endsWith('_complete')) return 'complete';
  if (status.endsWith('_failed')) return 'failed';
  return '';
}

function getNextAction(status: BatchStatus): { label: string; stage: 0 | 1 | 2 | 3 } | null {
  switch (status) {
    case 'preflight_review':
      return { label: 'Approve Preflight', stage: 0 };
    case 'uploaded':
    case 'stage1_failed':
      return { label: 'Run Stage 1: Load Data', stage: 1 };
    case 'stage1_complete':
    case 'stage2_failed':
      return { label: 'Run Stage 2: Calculate Indicators', stage: 2 };
    case 'stage2_complete':
    case 'stage3_failed':
      return { label: 'Run Stage 3: Calculate Ratings', stage: 3 };
    default:
      return null;
  }
}

function getStageStatus(batch: UploadBatch, stage: 1 | 2 | 3): 'pending' | 'queued' | 'running' | 'complete' | 'failed' {
  const prefix = `stage${stage}`;
  const status = batch.status;
  if (status === `${prefix}_queued`) return 'queued';
  if (status === `${prefix}_running`) return 'running';
  if (status === `${prefix}_complete`) return 'complete';
  if (status === `${prefix}_failed`) return 'failed';
  // Check if this stage completed earlier (batch moved past it)
  const stageNum = stage;
  const completedField = `stage${stageNum}_completed_at` as keyof UploadBatch;
  if (batch[completedField]) return 'complete';
  const errorField = `stage${stageNum}_error` as keyof UploadBatch;
  if (batch[errorField]) return 'failed';
  return 'pending';
}

function validateFiles(files: File[]): string | null {
  if (files.length === 0) return 'At least one file is required';
  if (files.length > MAX_FILES) return `Maximum ${MAX_FILES} files allowed`;
  for (const file of files) {
    const ext = file.name.lastIndexOf('.') >= 0
      ? file.name.slice(file.name.lastIndexOf('.')).toLowerCase()
      : '';
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      return `"${file.name}" has an unsupported format. Accepted: ${ACCEPTED_EXTENSIONS.join(', ')}`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `"${file.name}" exceeds 500MB limit`;
    }
  }
  return null;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const DataUpload: React.FC = () => {
  // Upload form state
  const [entityType, setEntityType] = useState<EntityType>('cooperativas_s1');
  const [period, setPeriod] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  // Upload progress (signed URL flow)
  const [uploadProgress, setUploadProgress] = useState<Record<string, { loaded: number; total: number }>>({});

  // Duplicate period warning
  const [periodWarning, setPeriodWarning] = useState<{ ratingsCount: number } | null>(null);

  // Batch list state
  const [batches, setBatches] = useState<UploadBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  const [triggeringStage, setTriggeringStage] = useState<Record<string, number | null>>({});

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingIdsRef = useRef<string[]>([]);

  // --------------------------------------------------------------------------
  // Data Fetching
  // --------------------------------------------------------------------------

  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listBatches({ limit: 50 });
      setBatches(response.batches);
    } catch (err) {
      console.error('Error fetching batches:', err);
      setError(err instanceof Error ? err.message : 'Failed to load batches');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  // --------------------------------------------------------------------------
  // Polling for running stages
  // --------------------------------------------------------------------------

  useEffect(() => {
    const pollingIds = batches
      .filter(b => b.status.endsWith('_queued') || b.status.endsWith('_running'))
      .map(b => b.batch_id)
      .sort();

    if (JSON.stringify(pollingIds) === JSON.stringify(pollingIdsRef.current)) return;
    pollingIdsRef.current = pollingIds;

    if (pollingIds.length === 0) return;

    const intervalId = setInterval(async () => {
      for (const batchId of pollingIdsRef.current) {
        try {
          const jobStatus = await getJobStatus(batchId);
          setBatches(prev =>
            prev.map(b =>
              b.batch_id === batchId ? { ...b, status: jobStatus.status } : b
            )
          );
          // On terminal state, fetch full batch for updated metrics
          if (jobStatus.status.endsWith('_complete') || jobStatus.status.endsWith('_failed')) {
            try {
              const full = await getBatch(batchId);
              setBatches(prev =>
                prev.map(b => b.batch_id === batchId ? full : b)
              );
            } catch {
              // Non-critical — status is already updated
            }
          }
        } catch (err) {
          console.warn(`Failed to poll status for batch ${batchId}:`, err);
        }
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [batches]);

  // --------------------------------------------------------------------------
  // Duplicate Period Check
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (!period || !/^\d{4}-\d{2}$/.test(period)) {
      setPeriodWarning(null);
      return;
    }

    let cancelled = false;
    const check = async () => {
      try {
        const result = await checkPeriod(period);
        if (!cancelled) {
          setPeriodWarning(result.hasExistingData ? { ratingsCount: result.ratingsCount } : null);
        }
      } catch {
        // Non-critical — don't block upload
        if (!cancelled) setPeriodWarning(null);
      }
    };

    // Debounce the check slightly
    const timeout = setTimeout(check, 300);
    return () => { cancelled = true; clearTimeout(timeout); };
  }, [period]);

  // --------------------------------------------------------------------------
  // File Handling
  // --------------------------------------------------------------------------

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
      e.target.value = ''; // Reset so same file can be re-selected
    }
  };

  const addFiles = (newFiles: File[]) => {
    setUploadError(null);
    const combined = [...selectedFiles, ...newFiles];
    const validationError = validateFiles(combined);
    if (validationError) {
      setUploadError(validationError);
      return;
    }
    setSelectedFiles(combined);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setUploadError(null);
  };

  // --------------------------------------------------------------------------
  // Upload
  // --------------------------------------------------------------------------

  const handleUpload = async () => {
    const validationError = validateFiles(selectedFiles);
    if (validationError) {
      setUploadError(validationError);
      return;
    }
    if (!period) {
      setUploadError('Please select a period');
      return;
    }

    // Confirm if data already exists for this period
    if (periodWarning) {
      const confirmed = window.confirm(
        `Data already exists for period ${period} (${periodWarning.ratingsCount} active ratings). ` +
        `Uploading new data and running the pipeline will replace existing ratings.\n\nContinue?`
      );
      if (!confirmed) return;
    }

    try {
      setUploading(true);
      setUploadError(null);
      setSuccessMessage(null);
      setUploadProgress({});

      // Step 1: Get signed URLs from backend
      const initResult = await initUpload({
        entityType,
        period,
        files: selectedFiles.map(f => ({ name: f.name, size: f.size, type: f.type })),
      });

      // Step 2: Upload each file directly to GCS using signed URLs
      const fileMap = new Map(selectedFiles.map(f => [f.name, f]));

      for (const upload of initResult.uploads) {
        const file = fileMap.get(upload.name);
        if (!file) {
          throw new Error(`File ${upload.name} not found in selected files`);
        }
        await uploadFileToGCS(
          upload.signedUrl,
          file,
          upload.contentType,
          (loaded, total) => {
            setUploadProgress(prev => ({
              ...prev,
              [upload.name]: { loaded, total },
            }));
          },
        );
      }

      // Step 3: Finalize — backend reads from GCS, detects types, runs preflight
      const batch = await finalizeBatch({
        batchId: initResult.batchId,
        entityType,
        period,
        files: initResult.uploads.map(u => ({
          name: u.name,
          gcsPath: u.gcsPath,
          contentType: u.contentType,
        })),
      });

      setBatches(prev => [batch, ...prev]);
      setSelectedFiles([]);
      setPeriod('');
      setUploadProgress({});
      setSuccessMessage(
        `Uploaded ${batch.files.length} file(s) for ${ENTITY_TYPE_LABELS[entityType]} — ${period}`
      );

      // Clear success message after 5s
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // --------------------------------------------------------------------------
  // Stage Triggers
  // --------------------------------------------------------------------------

  const handleRunStage = async (batchId: string, stage: 1 | 2 | 3) => {
    const stageFns = { 1: runStage1, 2: runStage2, 3: runStage3 };
    const queuedStatus = `stage${stage}_queued` as BatchStatus;

    try {
      setTriggeringStage(prev => ({ ...prev, [batchId]: stage }));

      await stageFns[stage](batchId);

      // Optimistically update to queued
      setBatches(prev =>
        prev.map(b =>
          b.batch_id === batchId ? { ...b, status: queuedStatus } : b
        )
      );
    } catch (err) {
      console.error(`Stage ${stage} trigger error:`, err);
      const message = err instanceof Error ? err.message : `Failed to trigger Stage ${stage}`;
      // Show error inline — re-fetch batch to get accurate status
      try {
        const updated = await getBatch(batchId);
        setBatches(prev => prev.map(b => b.batch_id === batchId ? updated : b));
      } catch {
        // Fall back to showing error
      }
      setError(message);
      setTimeout(() => setError(null), 5000);
    } finally {
      setTriggeringStage(prev => ({ ...prev, [batchId]: null }));
    }
  };

  // --------------------------------------------------------------------------
  // Approve Preflight
  // --------------------------------------------------------------------------

  const handleApprovePreflight = async (batchId: string) => {
    try {
      setTriggeringStage(prev => ({ ...prev, [batchId]: 0 }));
      const updated = await approvePreflight(batchId);
      setBatches(prev => prev.map(b => b.batch_id === batchId ? updated : b));
    } catch (err) {
      console.error('Approve preflight error:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve preflight');
      setTimeout(() => setError(null), 5000);
    } finally {
      setTriggeringStage(prev => ({ ...prev, [batchId]: null }));
    }
  };

  // --------------------------------------------------------------------------
  // Delete
  // --------------------------------------------------------------------------

  const handleDelete = async (batchId: string) => {
    if (!window.confirm('Delete this batch? Files will be removed from storage.')) return;

    try {
      await deleteBatch(batchId);
      setBatches(prev => prev.filter(b => b.batch_id !== batchId));
    } catch (err) {
      console.error('Delete error:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete batch');
      setTimeout(() => setError(null), 5000);
    }
  };

  // --------------------------------------------------------------------------
  // Clear Caches
  // --------------------------------------------------------------------------

  const handleClearCaches = async () => {
    try {
      await clearCaches();
      setSuccessMessage('Data caches cleared successfully. New data will appear on next page load.');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Clear caches error:', err);
      setError(err instanceof Error ? err.message : 'Failed to clear caches');
      setTimeout(() => setError(null), 5000);
    }
  };

  // --------------------------------------------------------------------------
  // Expand/Collapse
  // --------------------------------------------------------------------------

  const toggleExpand = (batchId: string) => {
    setExpandedBatches(prev => {
      const next = new Set(prev);
      if (next.has(batchId)) {
        next.delete(batchId);
      } else {
        next.add(batchId);
      }
      return next;
    });
  };

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="data-upload-page">
        <h1>Data Upload</h1>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="data-upload-page">
      <h1>Data Upload</h1>

      {error && <div className="du-error-message">{error}</div>}

      {/* ================================================================== */}
      {/* UPLOAD FORM */}
      {/* ================================================================== */}
      <div className="du-upload-card">
        <h2>Upload Data Files</h2>

        {successMessage && <div className="du-success-message">{successMessage}</div>}
        {uploadError && <div className="du-error-message">{uploadError}</div>}

        <div className="du-form-grid">
          <div className="du-form-group">
            <label htmlFor="entityType">Entity Type</label>
            <select
              id="entityType"
              value={entityType}
              onChange={e => setEntityType(e.target.value as EntityType)}
              disabled={uploading}
            >
              {ENTITY_TYPES.map(et => (
                <option key={et} value={et}>{ENTITY_TYPE_LABELS[et]}</option>
              ))}
            </select>
          </div>

          <div className="du-form-group">
            <label htmlFor="period">Period</label>
            <input
              id="period"
              type="month"
              value={period}
              onChange={e => setPeriod(e.target.value)}
              disabled={uploading}
              placeholder="YYYY-MM"
            />
          </div>
        </div>

        {periodWarning && (
          <div className="du-period-warning">
            Data already exists for period <strong>{period}</strong> ({periodWarning.ratingsCount} active ratings).
            Uploading and running the pipeline will replace existing ratings.
          </div>
        )}

        <div
          className={`du-drop-zone ${dragging ? 'dragging' : ''} ${selectedFiles.length > 0 ? 'has-files' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".csv,.txt,.xlsx,.xlsm"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <div className="du-drop-zone-content">
            <span className="du-drop-icon">📁</span>
            <p>Drop data files here or click to browse</p>
            <span className="du-drop-hint">CSV, TXT, or Excel (.xlsx/.xlsm). Max {MAX_FILES} files, 500MB each.</span>
          </div>
        </div>

        {selectedFiles.length > 0 && (
          <div className="du-file-list">
            {selectedFiles.map((file, index) => (
              <div key={`${file.name}-${index}`} className="du-file-item">
                <span className="du-file-name">📄 {file.name}</span>
                <span className="du-file-size">{formatFileSize(file.size)}</span>
                <button
                  className="du-file-remove"
                  onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                  disabled={uploading}
                  title="Remove file"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Upload progress */}
        {uploading && Object.keys(uploadProgress).length > 0 && (
          <div className="du-upload-progress">
            {Object.entries(uploadProgress).map(([name, { loaded, total }]) => {
              const pct = total > 0 ? Math.round((loaded / total) * 100) : 0;
              return (
                <div key={name} className="du-progress-item">
                  <div className="du-progress-label">
                    <span className="du-progress-name">{name}</span>
                    <span className="du-progress-pct">{pct}% ({formatFileSize(loaded)} / {formatFileSize(total)})</span>
                  </div>
                  <div className="du-progress-bar">
                    <div className="du-progress-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="du-upload-actions">
          <button
            className="du-upload-btn"
            onClick={handleUpload}
            disabled={uploading || selectedFiles.length === 0 || !period}
          >
            {uploading ? (
              <>
                <span className="du-spinner" />
                {Object.keys(uploadProgress).length > 0 ? 'Uploading to GCS...' : 'Initializing...'}
              </>
            ) : (
              `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`
            )}
          </button>
        </div>
      </div>

      {/* ================================================================== */}
      {/* BATCH LIST */}
      {/* ================================================================== */}
      <div className="du-batch-section">
        <div className="du-batch-header">
          <h2>Upload Batches</h2>
          <button className="du-refresh-btn" onClick={fetchBatches} title="Refresh">
            🔄
          </button>
        </div>

        {batches.length === 0 ? (
          <div className="du-empty">No batches uploaded yet.</div>
        ) : (
          <div className="du-batch-table-wrapper">
            <table className="du-batch-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Entity Type</th>
                  <th>Period</th>
                  <th>Files</th>
                  <th>Uploaded</th>
                  <th>Actions</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {batches.map(batch => (
                  <React.Fragment key={batch.batch_id}>
                    <tr className={expandedBatches.has(batch.batch_id) ? 'expanded' : ''}>
                      <td>
                        <span className={`du-status-badge ${getStatusCategory(batch.status)}`}>
                          {getStatusCategory(batch.status) === 'running' && (
                            <span className="du-spinner-small" />
                          )}
                          {BATCH_STATUS_LABELS[batch.status]}
                        </span>
                      </td>
                      <td>{ENTITY_TYPE_LABELS[batch.entity_type]}</td>
                      <td className="du-period-cell">{batch.period}</td>
                      <td>{batch.files.length} file{batch.files.length !== 1 ? 's' : ''}</td>
                      <td className="du-date-cell">{formatDate(batch.uploaded_at)}</td>
                      <td className="du-actions-cell">
                        <BatchActions
                          batch={batch}
                          triggeringStage={triggeringStage[batch.batch_id] ?? null}
                          onRunStage={handleRunStage}
                          onApprove={handleApprovePreflight}
                          onDelete={handleDelete}
                          onClearCaches={handleClearCaches}
                        />
                      </td>
                      <td>
                        <button
                          className="du-expand-btn"
                          onClick={() => toggleExpand(batch.batch_id)}
                          title={expandedBatches.has(batch.batch_id) ? 'Collapse' : 'Expand'}
                        >
                          {expandedBatches.has(batch.batch_id) ? '▲' : '▼'}
                        </button>
                      </td>
                    </tr>
                    {expandedBatches.has(batch.batch_id) && (
                      <tr className="du-expanded-row">
                        <td colSpan={7}>
                          <BatchDetail batch={batch} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const BatchActions: React.FC<{
  batch: UploadBatch;
  triggeringStage: number | null;
  onRunStage: (batchId: string, stage: 1 | 2 | 3) => void;
  onApprove: (batchId: string) => void;
  onDelete: (batchId: string) => void;
  onClearCaches: () => void;
}> = ({ batch, triggeringStage, onRunStage, onApprove, onDelete, onClearCaches }) => {
  const action = getNextAction(batch.status);
  const isTriggering = triggeringStage !== null;
  const isRunning = batch.status.endsWith('_queued') || batch.status.endsWith('_running');

  if (batch.status === 'stage3_complete') {
    return (
      <div className="du-action-buttons">
        <span className="du-complete-label">Complete</span>
        <button className="du-cache-btn" onClick={onClearCaches} title="Refresh data caches">
          Refresh Caches
        </button>
      </div>
    );
  }

  if (isRunning) {
    return <span className="du-running-label"><span className="du-spinner-small" /> Processing...</span>;
  }

  return (
    <div className="du-action-buttons">
      {action && action.stage === 0 && (
        <button
          className="du-stage-btn du-approve-btn"
          onClick={() => onApprove(batch.batch_id)}
          disabled={isTriggering}
        >
          {isTriggering ? (
            <><span className="du-spinner-small" /> Approving...</>
          ) : (
            action.label
          )}
        </button>
      )}
      {action && action.stage > 0 && (
        <button
          className={`du-stage-btn ${batch.status.endsWith('_failed') ? 'retry' : ''}`}
          onClick={() => onRunStage(batch.batch_id, action.stage as 1 | 2 | 3)}
          disabled={isTriggering}
        >
          {isTriggering ? (
            <><span className="du-spinner-small" /> Triggering...</>
          ) : (
            action.label
          )}
        </button>
      )}
      {(batch.status === 'uploaded' || batch.status === 'preflight_review') && (
        <button
          className="du-delete-btn"
          onClick={() => onDelete(batch.batch_id)}
          disabled={isTriggering}
          title="Delete batch"
        >
          🗑️
        </button>
      )}
    </div>
  );
};

const QUALITY_LABELS: Record<string, string> = {
  alta: 'Alta',
  media: 'Media',
  baja: 'Baja',
};

const DECIMAL_LABELS: Record<string, string> = {
  comma: 'Coma',
  dot: 'Punto',
  unknown: '—',
};

/** Stage 0 card in the timeline */
const Stage0Card: React.FC<{ batch: UploadBatch }> = ({ batch }) => {
  const hasAnalysis = !!batch.preflight_analysis;
  const needsReview = batch.status === 'preflight_review';
  const hasPT = batch.files.some(f => f.file_type === 'patrimonio_tecnico');

  let stateClass = 'pending';
  let icon = '⬜';
  let label = 'Pending';
  if (needsReview) {
    stateClass = 'review';
    icon = '⚠️';
    label = 'Review Required';
  } else if (hasAnalysis) {
    stateClass = 'complete';
    icon = '✅';
    label = 'Complete';
  }

  const cs = batch.preflight_analysis?.computed_stats;

  return (
    <div className={`du-stage-step ${stateClass}`}>
      <div className="du-stage-step-header">
        <span className="du-stage-number">Stage 0</span>
        <span className="du-stage-label">Pre-flight Analysis</span>
      </div>
      <div className="du-stage-step-status">
        {icon} <span>{label}</span>
      </div>
      {cs && (
        <div className="du-stage0-stats">
          <span className="du-stage0-stat">
            {cs.declared_period_found ? '✓' : '✗'} Period {cs.declared_period_found ? 'found' : 'missing'}
          </span>
          <span className="du-stage0-stat">
            {cs.unique_rucs} institutions
          </span>
          {cs.institution_cross_ref && (
            <span className="du-stage0-stat">
              {cs.institution_cross_ref.matched_count} in BQ
            </span>
          )}
          <span className="du-stage0-stat">
            Decimal: {DECIMAL_LABELS[cs.decimal_separator] || '—'}
          </span>
          <span className={`du-stage0-stat ${!hasPT ? 'du-stage0-stat-warning' : ''}`}>
            {hasPT ? '✓' : '✗'} PT Data {hasPT ? 'included' : 'missing'}
          </span>
        </div>
      )}
      {!hasPT && hasAnalysis && (
        <div className="du-stage0-pt-warning">
          S1 (Capital Adequacy) will be skipped — upload PT file for complete ratings
        </div>
      )}
    </div>
  );
};

const PreflightSection: React.FC<{ analysis: PreflightAnalysis; hasPT?: boolean }> = ({ analysis, hasPT = true }) => {
  const { resumen, calidad, alertas, recomendacion, computed_stats: cs } = analysis;

  return (
    <div className="du-detail-section du-preflight">
      <h4>Pre-flight Analysis Details</h4>

      {/* Computed stats (deterministic) */}
      {cs && (
        <div className="du-preflight-computed">
          <div className="du-preflight-computed-grid">
            <div className="du-preflight-computed-item">
              <span className="du-preflight-computed-label">Delimiter</span>
              <span className="du-preflight-computed-value">{cs.delimiter}</span>
            </div>
            <div className="du-preflight-computed-item">
              <span className="du-preflight-computed-label">Decimal</span>
              <span className="du-preflight-computed-value">{DECIMAL_LABELS[cs.decimal_separator]}</span>
            </div>
            <div className="du-preflight-computed-item">
              <span className="du-preflight-computed-label">Period</span>
              <span className={`du-preflight-computed-value ${cs.declared_period_found ? 'du-found' : 'du-missing'}`}>
                {cs.declared_period_found ? 'Found' : 'Not Found'}
              </span>
            </div>
            <div className="du-preflight-computed-item">
              <span className="du-preflight-computed-label">Sampling</span>
              <span className="du-preflight-computed-value">
                {cs.total_sampled_rows} rows ({cs.sample_positions.join(' + ')})
              </span>
            </div>
          </div>

          {cs.periods_found.length > 0 && (
            <div className="du-preflight-periods">
              <span className="du-preflight-periods-label">Periods found:</span>
              <span className="du-preflight-periods-list">{cs.periods_found.join(', ')}</span>
            </div>
          )}

          {cs.institution_cross_ref && (
            <div className="du-preflight-xref">
              <span className="du-preflight-xref-label">Institutions (BQ cross-ref):</span>
              <span className="du-preflight-xref-stat">
                {cs.institution_cross_ref.matched_count} of {cs.institution_cross_ref.total_sampled} exist (will be updated)
              </span>
              {cs.institution_cross_ref.unmatched_count > 0 && (
                <span className="du-preflight-xref-new">
                  {cs.institution_cross_ref.unmatched_count} new
                </span>
              )}
              {cs.institution_cross_ref.matched_names.length > 0 && (
                <div className="du-preflight-xref-names">
                  {cs.institution_cross_ref.matched_names.slice(0, 5).join(', ')}
                  {cs.institution_cross_ref.matched_names.length > 5 && ', ...'}
                </div>
              )}
            </div>
          )}

          {cs.expected_format.delimiter && (
            <div className="du-preflight-expected">
              <span className="du-preflight-expected-label">Expected format:</span>
              <span>Delim: {cs.expected_format.delimiter}</span>
              <span>Decimal: {cs.expected_format.decimal}</span>
              <span>{cs.expected_format.encoding}</span>
            </div>
          )}
        </div>
      )}

      {/* Summary stats (from Gemini) */}
      <div className="du-preflight-summary">
        <div className="du-preflight-stat">
          <span className="du-preflight-stat-value">{resumen.total_filas?.toLocaleString() ?? '—'}</span>
          <span className="du-preflight-stat-label">Filas</span>
        </div>
        <div className="du-preflight-stat">
          <span className="du-preflight-stat-value">{resumen.instituciones_count ?? '—'}</span>
          <span className="du-preflight-stat-label">Instituciones</span>
        </div>
        <div className="du-preflight-stat">
          <span className="du-preflight-stat-value">{resumen.codigos_cuenta_count ?? '—'}</span>
          <span className="du-preflight-stat-label">Cuentas</span>
        </div>
        <div className="du-preflight-stat">
          <span className="du-preflight-stat-value">{resumen.periodos_encontrados?.length ?? '—'}</span>
          <span className="du-preflight-stat-label">Periodos</span>
        </div>
      </div>

      {/* Quality badges */}
      <div className="du-preflight-quality">
        <div className="du-preflight-quality-item">
          <span className="du-preflight-quality-label">Completitud:</span>
          <span className={`du-quality-badge du-quality-${calidad.completitud}`}>
            {QUALITY_LABELS[calidad.completitud] || calidad.completitud}
          </span>
          <span className="du-preflight-quality-detail">{calidad.completitud_detalle}</span>
        </div>
        <div className="du-preflight-quality-item">
          <span className="du-preflight-quality-label">Consistencia:</span>
          <span className={`du-quality-badge du-quality-${calidad.consistencia}`}>
            {QUALITY_LABELS[calidad.consistencia] || calidad.consistencia}
          </span>
          <span className="du-preflight-quality-detail">{calidad.consistencia_detalle}</span>
        </div>
      </div>

      {/* Alerts */}
      {(alertas?.length > 0 || !hasPT) && (
        <div className="du-preflight-alerts">
          {!hasPT && (
            <div className="du-preflight-alert du-preflight-alert-advertencia">
              <span className="du-preflight-alert-icon">!</span>
              <span>
                No se incluyó archivo de Patrimonio Técnico (PT). El indicador S1 (Ratio de Capital Global)
                no será calculado, lo que puede afectar las calificaciones CAMEL. Suba un archivo PT (.xlsx/.xlsm)
                para obtener calificaciones completas.
              </span>
            </div>
          )}
          {alertas?.map((alert, idx) => (
            <div key={idx} className={`du-preflight-alert du-preflight-alert-${alert.tipo}`}>
              <span className="du-preflight-alert-icon">
                {alert.tipo === 'error' ? '!' : alert.tipo === 'advertencia' ? '!' : 'i'}
              </span>
              <span>{alert.mensaje}</span>
            </div>
          ))}
        </div>
      )}

      {/* Recommendation */}
      <div className={`du-preflight-recommendation ${recomendacion.proceder ? 'proceed' : 'review'}`}>
        <span className="du-preflight-recommendation-icon">
          {recomendacion.proceder ? '\u2713' : '\u26A0'}
        </span>
        <span>{recomendacion.mensaje}</span>
      </div>
    </div>
  );
};

const BatchDetail: React.FC<{ batch: UploadBatch }> = ({ batch }) => {
  return (
    <div className="du-detail">
      {/* File Details */}
      <div className="du-detail-section">
        <h4>Files</h4>
        <table className="du-file-details-table">
          <thead>
            <tr>
              <th>Filename</th>
              <th>Type</th>
              <th>Size</th>
              <th>Rows</th>
              <th>Hash</th>
            </tr>
          </thead>
          <tbody>
            {batch.files.map((file: BatchFile, idx: number) => (
              <tr key={idx}>
                <td>{file.filename}</td>
                <td>
                  <span className="du-file-type-badge">
                    {file.file_type === 'balance_sheet' ? 'Balance Sheet' : 'Patrimonio Técnico'}
                  </span>
                </td>
                <td>{formatFileSize(file.size_bytes)}</td>
                <td>{file.row_count.toLocaleString()}</td>
                <td className="du-hash" title={file.content_hash}>
                  {file.content_hash?.substring(0, 12) ?? '—'}...
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Stage Timeline (0-3) */}
      <div className="du-detail-section">
        <h4>Pipeline Stages</h4>
        <div className="du-stage-timeline du-stage-timeline-4">
          {/* Stage 0: Pre-flight Analysis */}
          <Stage0Card batch={batch} />

          {/* Stages 1-3 */}
          {([1, 2, 3] as const).map(stage => {
            const stageState = getStageStatus(batch, stage);
            const stageLabels = {
              1: 'Load Data',
              2: 'Calculate Indicators',
              3: 'Calculate Ratings',
            };
            const stageMetrics = {
              1: batch.stage1_rows_loaded != null ? `${batch.stage1_rows_loaded.toLocaleString()} rows` : null,
              2: batch.stage2_indicators_calculated != null ? `${batch.stage2_indicators_calculated.toLocaleString()} indicators` : null,
              3: batch.stage3_ratings_calculated != null ? `${batch.stage3_ratings_calculated.toLocaleString()} ratings` : null,
            };
            const stageStarted = batch[`stage${stage}_started_at` as keyof UploadBatch] as string | null;
            const stageCompleted = batch[`stage${stage}_completed_at` as keyof UploadBatch] as string | null;
            const stageError = batch[`stage${stage}_error` as keyof UploadBatch] as string | null;

            return (
              <div key={stage} className={`du-stage-step ${stageState}`}>
                <div className="du-stage-step-header">
                  <span className="du-stage-number">Stage {stage}</span>
                  <span className="du-stage-label">{stageLabels[stage]}</span>
                </div>
                <div className="du-stage-step-status">
                  {stageState === 'running' && <span className="du-spinner-small" />}
                  {stageState === 'complete' && '✅'}
                  {stageState === 'failed' && '❌'}
                  {stageState === 'queued' && '⏳'}
                  {stageState === 'pending' && '⬜'}
                  <span>{stageState.charAt(0).toUpperCase() + stageState.slice(1)}</span>
                </div>
                {stageMetrics[stage] && (
                  <div className="du-stage-metric">{stageMetrics[stage]}</div>
                )}
                {stageStarted && (
                  <div className="du-stage-time">Started: {formatDate(stageStarted)}</div>
                )}
                {stageCompleted && (
                  <div className="du-stage-time">Completed: {formatDate(stageCompleted)}</div>
                )}
                {stageError && (
                  <div className="du-stage-error">{stageError}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Pre-flight details (expanded from Stage 0) */}
      {batch.preflight_analysis && (
        <PreflightSection analysis={batch.preflight_analysis} hasPT={batch.files.some(f => f.file_type === 'patrimonio_tecnico')} />
      )}

      {/* Results (when stage3 complete) */}
      {batch.status === 'stage3_complete' && (
        <BatchResults batchId={batch.batch_id} />
      )}

      {/* Metadata */}
      <div className="du-detail-section du-metadata">
        <span>Batch ID: <code>{batch.batch_id}</code></span>
        <span>Uploaded by: {batch.uploaded_by_email}</span>
        <span>Model: {batch.risk_model_id}</span>
      </div>
    </div>
  );
};

const BatchResults: React.FC<{ batchId: string }> = ({ batchId }) => {
  const [results, setResults] = useState<BatchResultsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchResults = async () => {
      try {
        setLoading(true);
        const data = await getBatchResults(batchId);
        if (!cancelled) setResults(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load results');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchResults();
    return () => { cancelled = true; };
  }, [batchId]);

  if (loading) {
    return (
      <div className="du-detail-section">
        <h4>Results</h4>
        <div className="du-results-loading"><span className="du-spinner-small" /> Loading results...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="du-detail-section">
        <h4>Results</h4>
        <div className="du-results-error">{error}</div>
      </div>
    );
  }

  if (!results || results.totalRatings === 0) {
    return (
      <div className="du-detail-section">
        <h4>Results</h4>
        <div className="du-results-empty">No ratings found for this period.</div>
      </div>
    );
  }

  // Sort distribution by rating category
  const sortedCategories = Object.entries(results.distribution).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="du-detail-section">
      <h4>Results — Rating Distribution</h4>
      <div className="du-results-summary">
        <span className="du-results-total">{results.totalRatings} total ratings</span>
        <span className="du-results-period">Period: {results.period}</span>
      </div>
      <div className="du-results-distribution">
        {sortedCategories.map(([category, count]) => {
          const pct = ((count / results.totalRatings) * 100).toFixed(0);
          return (
            <div key={category} className="du-results-bar-row">
              <span className="du-results-category">{category}</span>
              <div className="du-results-bar-track">
                <div
                  className="du-results-bar-fill"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="du-results-count">{count} ({pct}%)</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DataUpload;
